import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { PageHeader, formatCZK, formatQty, formatDateTime } from "@/components/Shared";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import SearchBar, { applyTextFilter } from "@/components/SearchBar";
import { formatQtyWithPackage } from "@/lib/units";

export default function Warehouses() {
  const [warehouses, setWarehouses] = useState([]);
  const [branches, setBranches] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [products, setProducts] = useState([]);
  const [matBatches, setMatBatches] = useState([]);
  const [prodBatches, setProdBatches] = useState([]);
  const [matAvail, setMatAvail] = useState([]);
  const [prodAvail, setProdAvail] = useState([]);
  const [dispatches, setDispatches] = useState([]);
  const [branchStocks, setBranchStocks] = useState({}); // branch_id -> rows[]

  const load = async () => {
    const [w, b, m, p, mAvail, pAvail, disp, bs] = await Promise.all([
      api.get("/warehouses"),
      api.get("/branches"),
      api.get("/materials"),
      api.get("/products", { params: { include_deleted: true } }),
      api.get("/stock/material-availability"),
      api.get("/stock/product-availability"),
      api.get("/dispatches"),
      api.get("/branch-stock-all"),
    ]);
    setWarehouses(w.data);
    setBranches(b.data);
    setMaterials(m.data);
    setProducts(p.data);
    setMatAvail(mAvail.data);
    setProdAvail(pAvail.data);
    setDispatches(disp.data);
    setBranchStocks(bs.data || {});
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    (async () => {
      const matWh = warehouses.find((w) => w.type === "material");
      const prodWh = warehouses.find((w) => w.type === "production");
      if (matWh) {
        const r = await api.get("/stock/batches", { params: { warehouse_id: matWh.id } });
        setMatBatches(r.data);
      }
      if (prodWh) {
        const r = await api.get("/stock/batches", { params: { warehouse_id: prodWh.id } });
        setProdBatches(r.data);
      }
    })();
  }, [warehouses]);

  const matName = (id) => materials.find((x) => x.id === id)?.name || "—";
  const matUnit = (id) => materials.find((x) => x.id === id)?.unit || "";
  const prodName = (id) => products.find((x) => x.id === id)?.name || "—";
  const brName = (id) => branches.find((x) => x.id === id)?.name || "—";

  const [matSearch, setMatSearch] = useState("");
  const [prodSearch, setProdSearch] = useState("");
  const [branchSearch, setBranchSearch] = useState("");

  const matAvailFiltered = useMemo(
    () => applyTextFilter(matAvail, matSearch, (a) => [matName(a.material_id)]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [matAvail, matSearch, materials]
  );
  const matBatchesFiltered = useMemo(
    () => applyTextFilter(matBatches, matSearch, (b) => [b.batch_code, matName(b.material_id)]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [matBatches, matSearch, materials]
  );
  const prodAvailFiltered = useMemo(
    () => applyTextFilter(prodAvail, prodSearch, (a) => [prodName(a.product_id)]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [prodAvail, prodSearch, products]
  );
  const prodBatchesFiltered = useMemo(
    () => applyTextFilter(prodBatches, prodSearch, (b) => [b.batch_code, prodName(b.product_id)]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [prodBatches, prodSearch, products]
  );
  const branchesFiltered = useMemo(
    () => applyTextFilter(branches, branchSearch, (b) => [b.name, b.address]),
    [branches, branchSearch]
  );

  return (
    <div data-testid="warehouses-page">
      <PageHeader
        eyebrow="Sklady"
        title="Stav skladů"
        description="Materiálový sklad, výrobní sklad a pobočky. Každý příjem zakládá novou datumovou šarži s vlastní nákupní cenou."
      />

      <Tabs defaultValue="material" className="w-full">
        <TabsList className="bg-white border border-[#E5E1D8] rounded-lg p-1">
          <TabsTrigger value="material" data-testid="tab-material" className="data-[state=active]:bg-[#F9F6F0]">
            Materiálový sklad
          </TabsTrigger>
          <TabsTrigger value="production" data-testid="tab-production" className="data-[state=active]:bg-[#F9F6F0]">
            Výrobní sklad
          </TabsTrigger>
          <TabsTrigger value="branches" data-testid="tab-branches" className="data-[state=active]:bg-[#F9F6F0]">
            Pobočky
          </TabsTrigger>
        </TabsList>

        <TabsContent value="material" className="mt-6 space-y-6">
          <SearchBar
            value={matSearch}
            onChange={setMatSearch}
            placeholder="Hledat surovinu nebo šarži…"
            testid="warehouse-material-search"
          />
          <Section title="Aktuální dispozice surovin">
            {matAvailFiltered.length === 0 ? (
              <Empty text="Bez výsledků." />
            ) : (
              <Table
                head={["Surovina", "Množství", "Ø Nákupní cena", "Ø Hodnota"]}
                rows={matAvailFiltered.map((a) => [
                  matName(a.material_id),
                  formatQty(a.quantity, matUnit(a.material_id)),
                  formatCZK(a.avg_unit_price),
                  formatCZK(a.quantity * a.avg_unit_price),
                ])}
              />
            )}
          </Section>

          <Section title="Šarže surovin (datumové)">
            {matBatchesFiltered.length === 0 ? (
              <Empty text="Bez výsledků." />
            ) : (
              <Table
                head={["Kód šarže", "Datum", "Surovina", "Zbývá", "Příjem (původně)", "Nákupní cena", "Hodnota"]}
                rows={matBatchesFiltered.map((b) => [
                  <span className="mono text-xs">{b.batch_code}</span>,
                  b.batch_date,
                  matName(b.material_id),
                  formatQty(b.quantity, matUnit(b.material_id)),
                  formatQty(b.original_quantity, matUnit(b.material_id)),
                  formatCZK(b.unit_price),
                  formatCZK(b.quantity * b.unit_price),
                ])}
              />
            )}
          </Section>
        </TabsContent>

        <TabsContent value="production" className="mt-6 space-y-6">
          <SearchBar
            value={prodSearch}
            onChange={setProdSearch}
            placeholder="Hledat výrobek nebo šarži…"
            testid="warehouse-production-search"
          />
          <Section title="Dispozice výrobků">
            {prodAvailFiltered.length === 0 ? (
              <Empty text="Bez výsledků." />
            ) : (
              <Table
                head={["Výrobek", "Dispozice", "V expedici", "Ø Výrobní cena", "Hodnota"]}
                rows={prodAvailFiltered.map((a) => {
                  const product = products.find((p) => p.id === a.product_id);
                  const available = a.available ?? a.quantity;
                  const pending = a.pending_dispatch || 0;
                  return [
                    prodName(a.product_id),
                    <span className="text-[#376A4D] font-medium">
                      {formatQtyWithPackage(available, product, "ks")}
                    </span>,
                    pending > 0 ? (
                      <span className="text-[#9E4747]">{formatQtyWithPackage(pending, product, "ks")}</span>
                    ) : (
                      <span className="text-[#57534E]">—</span>
                    ),
                    formatCZK(a.avg_unit_cost),
                    formatCZK(available * a.avg_unit_cost),
                  ];
                })}
              />
            )}
          </Section>

          <Section title="Šarže výrobků">
            {prodBatchesFiltered.length === 0 ? (
              <Empty text="Bez výsledků." />
            ) : (
              <Table
                head={["Kód šarže", "Datum", "Výrobek", "Zbývá", "Vyrobeno", "Výrobní cena/ks", "Hodnota"]}
                rows={prodBatchesFiltered.map((b) => {
                  const product = products.find((p) => p.id === b.product_id);
                  return [
                    <span className="mono text-xs">{b.batch_code}</span>,
                    b.batch_date,
                    prodName(b.product_id),
                    formatQtyWithPackage(b.quantity, product, "ks"),
                    formatQtyWithPackage(b.original_quantity, product, "ks"),
                    formatCZK(b.unit_cost),
                    formatCZK(b.quantity * b.unit_cost),
                  ];
                })}
              />
            )}
          </Section>
        </TabsContent>

        <TabsContent value="branches" className="mt-6 space-y-6">
          <SearchBar
            value={branchSearch}
            onChange={setBranchSearch}
            placeholder="Hledat pobočku…"
            testid="warehouse-branches-search"
          />
          {branchesFiltered.length === 0 ? (
            <Empty text="Bez výsledků." />
          ) : (
            branchesFiltered.map((b) => {
              const stockRows = (branchStocks[b.id] || []).filter((r) => Math.abs(r.quantity) > 1e-9);
              return (
                <Section key={b.id} title={`Pobočka — ${b.name}`}>
                  {stockRows.length === 0 ? (
                    <Empty text="Sklad pobočky je prázdný." />
                  ) : (
                    <Table
                      head={["Výrobek", "Skladem", "Prům. cena/ks", "Hodnota"]}
                      rows={stockRows.map((r) => {
                        const product = products.find((p) => p.id === r.product_id);
                        return [
                          prodName(r.product_id),
                          formatQtyWithPackage(r.quantity, product, "ks"),
                          formatCZK(r.avg_unit_cost),
                          formatCZK(r.total_value),
                        ];
                      })}
                    />
                  )}
                </Section>
              );
            })
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="card-cafe p-6">
      <div className="label-eyebrow mb-4">{title}</div>
      {children}
    </div>
  );
}

function Table({ head, rows }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-[#E5E1D8]">
      <table className="w-full text-sm">
        <thead className="bg-[#F9F6F0]">
          <tr>
            {head.map((h, i) => (
              <th key={i} className="p-3 text-left text-[#57534E] font-medium text-xs uppercase tracking-[0.1em]">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, idx) => (
            <tr key={idx} className="border-t border-[#E5E1D8] hover:bg-[#F9F6F0]/60">
              {r.map((c, i) => (
                <td key={i} className="p-3 align-top">
                  {c}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Empty({ text }) {
  return <div className="text-sm text-[#57534E] py-8 text-center">{text}</div>;
}
